import React from 'react';
import { Navigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import featureFlags from '../config/featureFlags';

// Helper function (can be moved to a utils file later)
const hasRole = (userRole, roleToCheck) => {
  const roles = Array.isArray(userRole) ? userRole : [userRole];
  return roles.includes(roleToCheck);
};

const AuthRedirector = ({ isLoading, user, userRole }) => {
  if (isLoading) {
    // Still loading user data, show spinner
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    // Loading finished, but no user object (e.g., invalid token cleared), go to login
    console.log("AuthRedirector: No user found after loading, redirecting to login.");
    return <Navigate to="/login" replace />;
  }

  // Therapist-Led Mode (Spec C): clients whose service group resolves to
  // clientUIMode === 'patient_portal' go to the patient portal shell instead
  // of the standard client app. Therapists / admins / supervisors keep their
  // dashboards regardless — only clients see the portal swap.
  if (hasRole(userRole, 'client') && user.effectiveClientUIMode === 'patient_portal') {
    console.log('AuthRedirector: clientUIMode=patient_portal, redirecting to /portal.');
    return <Navigate to="/portal" replace />;
  }

  // User data loaded, perform role-based redirect
  // Check highest priority role first
  if (hasRole(userRole, 'admin')) {
    console.log("AuthRedirector: Admin detected, redirecting to admin dashboard.");
    return <Navigate to="/admin/dashboard" replace />;
  } else if (hasRole(userRole, 'clinical_supervisor')) {
    console.log("AuthRedirector: Clinical Supervisor detected, redirecting to clinical supervisor dashboard.");
    return <Navigate to="/clinical-supervisor/dashboard" replace />;
  } else if (hasRole(userRole, 'researcher')) {
    console.log("AuthRedirector: Researcher detected, redirecting to researcher dashboard.");
    return <Navigate to="/researcher/dashboard" replace />;
  } else if (hasRole(userRole, 'therapist')) {
    console.log("AuthRedirector: Therapist detected, redirecting to therapist dashboard.");
    return <Navigate to="/therapist/dashboard" replace />;
  } else if (hasRole(userRole, 'training_admin')) {
    console.log("AuthRedirector: Training Admin detected, redirecting to training admin dashboard.");
    return <Navigate to="/training/admin" replace />;
  } else if (hasRole(userRole, 'lecturer')) {
    console.log("AuthRedirector: Lecturer detected, redirecting to lecturer dashboard.");
    return <Navigate to="/training/lecturer" replace />;
  } else if (hasRole(userRole, 'student')) {
    console.log("AuthRedirector: Student detected, redirecting to training dashboard.");
    return <Navigate to="/training" replace />;
  } else {
    // For clients, redirect to dashboard if feature flag is enabled, otherwise chat
    const defaultRoute = featureFlags.NEW_DASHBOARD ? "/dashboard" : "/chat";
    console.log(`AuthRedirector: Client or unknown role detected, redirecting to ${defaultRoute}.`);
    return <Navigate to={defaultRoute} replace />;
  }
};

export default AuthRedirector;
